import React, { useState, useEffect, MutableRefObject, useCallback, useMemo } from "react";
// Materials
import { GridColDef, GridRowParams, GridRowSelectionModel, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
// Components
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import RowFeedback from "@/Common/helpers/RowFeedback";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import ErrorMessagePopover from "./ErrorMessagePopover";
// Types
import { IGetPayrollSubmitList } from "@/Washouts/types/PayrollClosing/queries";
import { ICommissionCycle, IPayPeriod } from "@/Common/api/types/CommissionCycle";
import { COLUMN_RESIZE_FACTOR, ONE_HUNDRED_FORTY, ONE_HUNDRED_THIRTY, ONE_HUNDRED_TWENTY } from "@/Constants/tables";
import { IPayrollClosingSideBarSelected } from "@/Washouts/types/PayrollClosing/PayrollClosingSideBar";
import { DEFAULT_HEADER_HEIGHT } from "@/Washouts/constants";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { useLocation } from "@/Common/hooks/Location/useLocation";

const HeaderClassName = "table-header-top-aligned show-as-column";
const CellClassName = " user-page-table-cell";

const checkDisabledByOrder = ({
	customId,
	data,
	position,
	payPeriodId,
}: {
	customId: string;
	data?: IGetPayrollSubmitList[] | null;
	position: string;
	payPeriodId: string;
}) => {
	if (!data?.length) {
		return false;
	}
	const positionData = data.filter((item) => item.position === position && item.payPeriodId === payPeriodId && item.status !== "DELETED");
	if (positionData?.length <= 1) {
		return false;
	}
	const sortedPositionData = positionData.sort((a, b) => {
		const startDateComparison = new Date(a?.dateRange?.[0]).getTime() - new Date(b?.dateRange?.[0]).getTime();
		if (startDateComparison !== 0) {
			return startDateComparison;
		}
		return new Date(a?.dateRange?.[1]).getTime() - new Date(b?.dateRange?.[1]).getTime();
	});
	const partnerIndex = sortedPositionData.findIndex((item) => {
		const internalCustomId = `${new Date(item?.dateRange?.[0])?.getTime()}-${new Date(item?.dateRange?.[1])?.getTime()}`;
		return internalCustomId !== customId;
	});
	return partnerIndex > 0;
};

const buildColumnDefinitions = (timezone: string) => {
	const columns: GridColDef[] = [
		{ field: "_id", headerName: "ID", width: 50, filterable: false },
		{
			field: "submitter",
			headerName: "Submitted by",
			width: 160,
			sortable: false,
			headerClassName: HeaderClassName,
			cellClassName: CellClassName,
			valueGetter: (value: { fullName: string }) => {
				return value?.fullName;
			},
		},
		{
			field: "statusDate",
			headerName: "Submission Date",
			type: "date",
			width: 200,
			sortable: false,
			headerClassName: HeaderClassName,
			cellClassName: CellClassName,
			valueGetter: (value) => getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, timezone, DATE_FORMAT.fullAbbrTime12h),
		},
		{
			field: "dateRange",
			headerName: "Date Range",
			type: "string",
			width: 150,
			sortable: false,
			headerClassName: HeaderClassName,
			cellClassName: CellClassName,
			valueFormatter: (value: string) => {
				if (value?.length !== 2) {
					return "";
				}
				const [startDate, endDate] = value;
				return `${getFormattedDate(startDate, "", DATE_FORMAT.monthShortDateOrdinal)} - ${getFormattedDate(
					endDate,
					"",
					DATE_FORMAT.monthShortDateOrdinal
				)}`;
			},
		},
		{
			field: "status",
			headerName: "Status",
			width: ONE_HUNDRED_TWENTY,
			sortable: false,
			headerClassName: HeaderClassName,
			cellClassName: `${CellClassName} capitalize`,
			renderCell: ({ value, row }) => {
				const [firstItem, ...rest] = row.errors ?? [];
				const content = rest.reduce(
					(acc: string[], error: string) => {
						return [...acc, <br key={error} />, error];
					},
					[firstItem]
				);
				const isError = value === "ERROR SUBMITTING" || value === "ERROR DELETING";
				return isError ? <ErrorMessagePopover errorMessage={content} status={value} /> : undefined;
			},
			valueFormatter: (value: string) => {
				return value?.toLowerCase();
			},
		},
		{
			field: "position",
			headerName: "Position",
			minWidth: ONE_HUNDRED_FORTY,
			width: ONE_HUNDRED_FORTY,
			maxWidth: ONE_HUNDRED_FORTY * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: false,
			headerClassName: HeaderClassName,
			cellClassName: CellClassName,
		},
		{
			field: "displayId",
			headerName: "Company ID",
			minWidth: ONE_HUNDRED_THIRTY,
			width: ONE_HUNDRED_THIRTY,
			maxWidth: ONE_HUNDRED_THIRTY * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: false,
			headerClassName: HeaderClassName,
			cellClassName: CellClassName,
		},
		{
			field: "providerPayPeriodId",
			headerName: "Pay Period ID",
			width: ONE_HUNDRED_FORTY,
			sortable: false,
			headerClassName: HeaderClassName,
			cellClassName: CellClassName,
		},
	];
	return columns;
};

type PayrollSubmitTableProps = {
	data?: IGetPayrollSubmitList[] | null;
	allowDeletion?: boolean;
	disabled?: boolean;
	selectedItem?: IPayrollClosingSideBarSelected | null;
	commissionCycle?: ICommissionCycle;
	payPeriod?: IPayPeriod;
	showSubmitted?: (id: string) => void;
	deleteSubmitted?: (ids: GridRowSelectionModel, apiRef: MutableRefObject<GridApiPremium>) => void;
};

export default function PayrollSubmitTable(props: Readonly<PayrollSubmitTableProps>) {
	const apiRef = useGridApiRef();

	const [rows, setRows] = useState(props.data ?? []);
	const [rowModesModel, setRowModesModel] = useState({});
	const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);

	const { timezone } = useLocation();
	const columns = useMemo(() => buildColumnDefinitions(timezone), [timezone]);

	const handleDeleteClick = async (ids: GridRowSelectionModel) => {
		ids.forEach((id) => {
			RowFeedback?.skeleton(id, apiRef)?.delete()?.display();
		});

		props.deleteSubmitted?.(ids, apiRef);
	};

	const handleRowClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
		const target = event.target as EventTarget & { parentNode: HTMLElement } & { parentNode: { parentNode: HTMLElement } };
		const rowId = target.parentNode.dataset.id ?? target.parentNode.parentNode.dataset.id ?? "";
		if (rowId && props.showSubmitted) {
			props.showSubmitted(rowId);
		}
	};

	const handleRowSelected = (newSelection: GridRowSelectionModel) => {
		setSelectedRows(newSelection);
	};

	const isRowSelectable = useCallback(
		(params: GridRowParams<GridValidRowModel>) => {
			const isSubmitted = params?.row?.status === "SUBMITTED";
			const isCycleFinalized = props.commissionCycle?.status === "FINALIZED";
			const isProviderCompleted = props.payPeriod?.status === "COMPLETED";

			const isDisabledByOrder = checkDisabledByOrder({
				customId: `${new Date(params?.row?.dateRange[0])?.getTime()}-${new Date(params?.row?.dateRange[1])?.getTime()}`,
				data: props.data,
				position: params.row.position,
				payPeriodId: params.row.payPeriodId,
			});

			const shouldDisable = !isSubmitted || isProviderCompleted || isCycleFinalized || isDisabledByOrder;
			if (!props.disabled && !shouldDisable) {
				return true;
			}
			return false;
		},
		[props.commissionCycle?.status, props.payPeriod?.status, props.data, props.disabled]
	);

	useEffect(() => {
		setRows(props.data ?? []);
		return () => {
			setRows([]);
		};
	}, [props]);

	return (
		<DataSmartTable
			columns={columns}
			rows={rows}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			rowHeight={28}
			apiRef={apiRef}
			hideFooter
			disableColumnMenu
			disableColumnResize
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={props.allowDeletion}
			isRowSelectable={isRowSelectable}
			onRowSelectionModelChange={handleRowSelected}
			rowModesModel={rowModesModel}
			onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
			initialState={{
				sorting: {
					sortModel: [{ field: "statusDate", sort: "desc" }],
				},
			}}
			slots={{
				noRowsOverlay: NoRowsOverlay,
				toolbar: CommonToolbar,
			}}
			slotProps={{
				toolbar: {
					filter: {
						hide: !!selectedRows.length,
						disabled: false,
					},
					search: {
						data: rows,
						hide: !!selectedRows.length,
						disabled: false,
					},
					delete: {
						onDeleteAction: handleDeleteClick,
						deleteTitle: "Remove",
						hide: !selectedRows.length,
						disabled: false,
					},
				},
				noRowsOverlay: {
					title: "No Payroll submitted this commission cycle",
				},
				row: {
					onClick: handleRowClick,
				},
			}}
			customProps={{
				tableContainerStyle: { height: "100%", width: "100%" },
				tableClassName: {
					"functional-table": true,
					"[&_.toolbar-actions]:!right-0": true,
					"[&_.MuiDataGrid-iconSeparator]:!mt-[7px]": true,
					"!top-[-28px]": true,
					"[&_.MuiDataGrid-main]:!mt-[20px]": true,
				},
				hide: false,
			}}
			columnVisibilityModel={{
				_id: false,
			}}
		/>
	);
}
